import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { timesheetService } from '@/services/timesheets/timesheetService';
import { timesheetQueryKeys } from './queryKeys';
import type {
  TimesheetFilters,
  PaginatedTimeEntries,
  TimeEntryWithComments,
} from '@/services/timesheets/types';

interface AccumulatedResult {
  entries: TimeEntryWithComments[];
  totalCount: number;
  hasMore: boolean;
  isLoadingFirstPage: boolean;
  isFetchingAny: boolean;
}

/**
 * Cache-derived "Load More" accumulator for company timesheets.
 *
 * Issues one TanStack query per loaded page (`0..pageCount-1`) with the same
 * filters. Each page is independently cached, refetched on invalidation, and
 * concatenated (dedup'd by `id`, last-write-wins) into a single rendered list.
 *
 * Replaces the previous client-side `accumulatedEntries` state in
 * `ManagerTimesheets.tsx`, which was append-only and froze status changes on
 * already-rendered rows (e.g. an approved entry kept showing `pending`
 * Approve/Reject buttons across sessions and devices).
 */
export function useAccumulatedCompanyTimeEntries(
  baseFilters: Omit<TimesheetFilters, 'page' | 'limit'>,
  pageCount: number,
  pageSize: number,
): AccumulatedResult {
  const safePageCount = Math.max(1, Math.min(pageCount, 20)); // hard cap fan-out

  const queries = useMemo(() => {
    return Array.from({ length: safePageCount }, (_, page) => {
      const filters: TimesheetFilters = {
        ...baseFilters,
        page,
        limit: pageSize,
      };
      return {
        queryKey: timesheetQueryKeys.companyTimeEntriesWithComments(filters),
        queryFn: () => timesheetService.getCompanyTimeEntriesWithComments(filters),
        staleTime: 2 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      };
    });
    // baseFilters identity intentionally treated as caller's responsibility;
    // we depend on its primitives to keep query identity stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    baseFilters.month?.getTime(),
    baseFilters.employeeId,
    baseFilters.payrollStatus,
    baseFilters.status,
    baseFilters.timezone,
    safePageCount,
    pageSize,
  ]);

  const results = useQueries({ queries });

  return useMemo(() => {
    const seen = new Set<string>();
    const entries: TimeEntryWithComments[] = [];

    for (const r of results) {
      const data = r.data as PaginatedTimeEntries | undefined;
      if (!data) continue;
      for (const e of data.entries) {
        if (seen.has(e.id)) continue;
        seen.add(e.id);
        entries.push(e);
      }
    }

    // Use the latest resolved page for pagination metadata.
    let totalCount = 0;
    let hasMore = false;
    for (let i = results.length - 1; i >= 0; i--) {
      const data = results[i].data as PaginatedTimeEntries | undefined;
      if (data) {
        totalCount = data.totalCount;
        hasMore = data.hasMore;
        break;
      }
    }

    return {
      entries,
      totalCount,
      hasMore,
      isLoadingFirstPage: results[0]?.isLoading ?? false,
      isFetchingAny: results.some((r) => r.isFetching),
    };
  }, [results]);
}
